import { Injectable, inject } from '@angular/core'
import { DatePipe } from '@angular/common'
import { NAVIGATOR_DATE_DISPLAY_FORMAT } from '@app/core/constants/app.constants'
import {
    BehaviorSubject,
    catchError,
    finalize,
    map,
    Observable,
    of,
    shareReplay,
    throwError,
} from 'rxjs'
import {
    IDepartment,
    ILeadInfo,
    ITeamMember,
    ITeamMemberWithTerritory,
    IVisitObjective,
    IVisitTimeSlot,
    IUsersLeadsApiResponse,
    IUsersLeadsApiData,
    IUserLeadsApiUser,
    IUserLeadItemApi,
    ILeadsInfoApiData,
    ILeadsInfoApiResponse,
    ILeadReassignmentRequest,
    ILeadReassignmentResponse,
    ILeadAssignmentRequest,
    ILeadAssignmentResponse,
    IClientLead,
    IAggregatedLeadsApiResponse,
    IAggregatedLeadItem,
    IUpdateLeadStatusRequest,
    IUpdateLeadStatusResponse,
} from '../models/lead.model'
import {
    IUnassignedClient,
    IUnassignedClientsApiResponse,
    IUnassignedClientApiItem,
    IUnassignedClientsPaginationApi,
} from '../models/lead.model'
import { HttpClient, HttpParams } from '@angular/common/http'
import { environment } from 'src/environments/environment'
import { ELeadStageTitle } from '@app/core/enum/client.enum'
import { LEAD_STAGES } from '@app/core/constants/app.constants'
import { API_ENDPOINTS } from '@app/core/constants/api.constants'
import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'
import {
    mapStageIdToStage as mapStageIdToStageHelper,
    mapStageNameToStage as mapStageNameToStageHelper,
    getClientLeadLostDeferredLabelFromParts,
    filterStageQualifiedPipelineLeads,
} from '../helpers/lead-list.helper'

@Injectable({
    providedIn: 'root',
})
export class LeadsService {
    private leadsSubject = new BehaviorSubject<ILeadInfo[]>([])
    public leads$ = this.leadsSubject.asObservable()

    // If you want to store leads locally
    private leads: ILeadInfo[] = []

    private usersLeadsInflight = new Map<string, Observable<IUsersLeadsApiData>>()
    private http = inject(HttpClient)
    private datePipe = inject(DatePipe)
    private readonly aggregatedLeadAssignUrl = `${environment.apiBaseUrl}${API_ENDPOINTS.AGGREGATED_LEAD_ASSIGN}`

    getLeads = (): Observable<ILeadInfo[]> => {
        return this.http.get<{ leads?: ILeadInfo[] }>('/assets/mock/leads.json').pipe(
            map((response) => response.leads ?? []),
            catchError((error) => {
                return of([])
            }),
        )
        /** will be replaced with the original API during the API integration  */
    }

    /**
     * Fetch client leads for one or more organisation codes with optional filters
     * API: /api/aggregated/v1/leads
     */
    getClientLeads(
        organisationCodes: string | string[],
        filters?: {
            academicYear?: number | string
            stageId?: number
            statusType?: string
        },
        clientName?: string,
    ): Observable<{
        leads: IClientLead[]
        totalLeads: number
        filteredLeads: number
        territoryId?: number
    }> {
        const normalizedOrganisationCodes = this.normalizeOrganisationCodes(organisationCodes)
        if (!normalizedOrganisationCodes) {
            return of({ leads: [], totalLeads: 0, filteredLeads: 0 })
        }

        const params = this.buildClientLeadParams(normalizedOrganisationCodes, filters)
        const url = `${environment.apiUrl}${API_ENDPOINTS.AGGREGATED_LEAD}`

        return this.http.get<IAggregatedLeadsApiResponse>(url, { params }).pipe(
            map(({ success, data }) => {
                if (!success || !data?.length) {
                    return { leads: [], totalLeads: 0, filteredLeads: 0 }
                }

                const { organisation_info, leads = [] } = data[0]
                const fallbackOrganisationCode = normalizedOrganisationCodes.split(',')[0] || ''
                const orgCode = organisation_info?.organisation_code || fallbackOrganisationCode

                return {
                    leads: leads.map((lead) =>
                        this.mapAggregatedLeadToClientLead(lead, orgCode, clientName),
                    ),
                    totalLeads: organisation_info?.total_leads ?? leads.length,
                    filteredLeads: organisation_info?.filtered_leads ?? leads.length,
                    territoryId: organisation_info?.territory_id,
                }
            }),
            // Let caller handle API errors (e.g., 429 rate limiting) so UI can show a proper message.
            catchError((error) => throwError(() => error)),
        )
    }

    /**
     * Build HTTP params for client leads API request
     */
    private buildClientLeadParams(
        organisationCodes: string,
        filters?: {
            academicYear?: number | string
            stageId?: number
            statusType?: string
        },
    ): HttpParams {
        let params = new HttpParams().set('organisation_codes', organisationCodes)

        if (filters?.academicYear) {
            const year =
                typeof filters.academicYear === 'string'
                    ? this.extractYearFromAcademicYear(filters.academicYear)
                    : filters.academicYear

            if (year) {
                params = params.set('academic_year', year.toString())
            }
        }

        if (filters?.stageId != null) {
            params = params.set('stage_id', filters.stageId.toString())
        }

        if (filters?.statusType) {
            params = params.set('status_type', filters.statusType.toLowerCase())
        }

        return params
    }

    /**
     * Normalize organisation code input into comma-separated string.
     */
    private normalizeOrganisationCodes(organisationCodes: string | string[]): string {
        const values = Array.isArray(organisationCodes)
            ? organisationCodes
            : organisationCodes.split(',')

        return values
            .map((code) => code.trim())
            .filter((code) => code.length > 0)
            .join(',')
    }

    /**
     * Map API lead item to IClientLead interface
     */
    private mapAggregatedLeadToClientLead(
        apiLead: IAggregatedLeadItem,
        orgCode: string,
        clientName?: string,
    ): IClientLead {
        const stage = mapStageIdToStageHelper(apiLead.stage_id)
        const lostOrDeferredLabel = getClientLeadLostDeferredLabelFromParts(apiLead.lead_status)
        const stageNameForUi = lostOrDeferredLabel ?? (apiLead.stage_name || '')
        // Format academic year: 2024 -> 2024-2025 (for display)
        const academicYear = this.formatAcademicYear(apiLead.academic_year)
        // Use just the year for lead name format: 2025 instead of 2025-2026
        const academicYearForName = apiLead.academic_year?.toString() || ''

        // Format lead name: clientname_academicyear if academic year exists, otherwise just clientname
        let leadName: string
        if (clientName) {
            leadName = academicYearForName ? `${clientName}_${academicYearForName}` : clientName
        } else {
            leadName = academicYearForName
                ? `Lead #${apiLead.lead_id}_${academicYearForName}`
                : `Lead #${apiLead.lead_id}`
        }

        // Extract product names
        const products = apiLead.product_names ?? []

        return {
            id: apiLead.lead_id.toString(),
            org_code: orgCode,
            leadName,
            statusLabel: stageNameForUi,
            stage,
            stageName: stageNameForUi,
            leadStatus: apiLead.lead_status ?? null,
            assignedTo: {
                id: apiLead.assigned_user_id || '',
                name:
                    apiLead.assigned_to_username?.trim() ||
                    apiLead.assigned_to_user_name?.trim() ||
                    apiLead.assigned_user_name?.trim() ||
                    '',
                avatar: null,
            },
            academicYear,
            products,
            createdAt: apiLead.created_at || '',
        }
    }

    /**
     * Format academic year from number to "YYYY-YYYY" format
     * Example: 2024 -> "2024-2025"
     */
    private formatAcademicYear(year: number | null | undefined): string {
        if (!year) return ''
        const nextYear = year + 1
        return `${year}-${nextYear}`
    }

    /**
     * Extract year from academic year string (e.g., "2024-2025" -> 2024)
     */
    private extractYearFromAcademicYear(academicYear: string): number | null {
        if (!academicYear) return null

        // Handle format like "2024-2025" or just "2024"
        const yearMatch = academicYear.match(REGEX_PATTERNS.ACADEMIC_YEAR_EXTRACT)
        if (yearMatch) {
            return parseInt(yearMatch[1], 10)
        }

        // Try parsing as number directly
        const year = parseInt(academicYear, 10)
        return isNaN(year) ? null : year
    }

    /**
     * Fetch users-leads for the given user id (RM or ZM).
     * Endpoint: {apiBaseUrl}/api/aggregated/v1/leads/{user_id}/users-leads
     */
    getUsersLeads(
        userId: string,
        page?: number,
        pageSize?: number,
        academicYear?: number,
        options?: {
            /** Optional backend stage filter for users-leads endpoint. */
            stageIds?: number[]
        },
    ): Observable<IUsersLeadsApiData> {
        const baseUrl = `${environment.apiBaseUrl}/api/aggregated/v1/leads/${encodeURIComponent(userId)}/users-leads`
        const params: string[] = []
        if (page && page > 0) params.push(`page=${encodeURIComponent(String(page))}`)
        if (pageSize && pageSize > 0) params.push(`limit=${encodeURIComponent(String(pageSize))}`)
        if (academicYear && academicYear > 0)
            params.push(`academic_year=${encodeURIComponent(String(academicYear))}`)
        if (options?.stageIds?.length) {
            params.push(`stage_ids=${encodeURIComponent(options.stageIds.join(','))}`)
        }
        const url = params.length ? `${baseUrl}?${params.join('&')}` : baseUrl

        const existing = this.usersLeadsInflight.get(url)
        if (existing) {
            return existing
        }

        const request$ = this.http.get<IUsersLeadsApiResponse>(url).pipe(
            map((resp) => resp?.data ?? []),
            shareReplay({
                bufferSize: 1,
                refCount: true,
            }),
            finalize(() => {
                this.usersLeadsInflight.delete(url)
            }),
        )
        this.usersLeadsInflight.set(url, request$)
        return request$
    }

    /**
     * Fetch leads by ids for funnel-filtered view.
     * Endpoint: GET /api/aggregated/v1/leads/info?lead_ids=1,2,3&page=1&limit=10
     */
    getLeadsInfo(leadIds: number[], page = 1, limit = 10): Observable<ILeadsInfoApiData> {
        if (!leadIds?.length) {
            return of({
                leads: [],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    total_leads: 0,
                    leads_per_page: limit,
                },
            })
        }
        const baseUrl = `${environment.apiBaseUrl}${API_ENDPOINTS.LEADS_INFO}`
        const params = new HttpParams()
            .set('lead_ids', leadIds.join(','))
            .set('page', String(page))
            .set('limit', String(limit))
        return this.http
            .get<ILeadsInfoApiResponse>(baseUrl, { params })
            .pipe(map((resp) => resp.data))
    }

    /** Map API user block to UI team member */
    mapApiUserToTeamMember(
        user: IUserLeadsApiUser,
        productMap?: Map<number, string>,
    ): ITeamMemberWithTerritory {
        const clients: ILeadInfo[] = (user.leads || []).map((lead) =>
            this.mapApiLeadToLeadInfo(lead, productMap),
        )
        return {
            id: user.user_id,
            name: user.user_name ?? '-',
            role: user.role_name ?? '-',
            leadCount: filterStageQualifiedPipelineLeads(clients).length,
            clients,
            territory_id: user.territory_id ?? undefined,
            territory_name: user.territory_name ?? undefined,
        }
    }

    /**
     * Map API lead to UI lead card (users-leads items).
     * `order_id` / `order_status` come from the API and drive S4 proposal CTAs on kanban and elsewhere;
     * they match lead-detail behaviour. `noOfProposalsDrafted` is not set here (see lead-table vs kanban divergence).
     */
    mapApiLeadToLeadInfo(lead: IUserLeadItemApi, productMap?: Map<number, string>): ILeadInfo {
        const textOrNA = (value: unknown): string => {
            if (value === null || value === undefined) return '-'
            const str = String(value).trim()
            /* Stage Name is not available in the API response, so we are using a default value */
            /* Prospecting is the default stage name Need to be changed after the Stage movement integration */
            return str.length ? str : '-'
        }

        const matchedStage = mapStageNameToStageHelper(lead.current_stage?.stage_name ?? '')

        // Compute product names from pitched_products when mapping is available
        const productsDisplay = (() => {
            const raw = lead.pitched_products as unknown
            if (!productMap) {
                return raw === null || raw === undefined ? '-' : String(raw)
            }
            const ids: number[] = Array.isArray(raw)
                ? (raw as unknown[]).map((v) => Number(v)).filter((n) => Number.isFinite(n))
                : typeof raw === 'string'
                  ? (() => {
                        const s = String(raw).trim()
                        if (s.startsWith('[') && s.endsWith(']')) {
                            try {
                                const arr = JSON.parse(s)
                                return Array.isArray(arr)
                                    ? arr.map((v) => Number(v)).filter((n) => Number.isFinite(n))
                                    : []
                            } catch {}
                        }
                        return s
                            .split(',')
                            .map((t) => Number(t.trim()))
                            .filter((n) => Number.isFinite(n))
                    })()
                  : typeof raw === 'number'
                    ? [raw]
                    : []
            if (!ids.length) return '-'
            const names = ids
                .map((id) => productMap.get(id) || String(id))
                .filter((v) => v && String(v).trim().length > 0)
            return names.length ? names.join(', ') : '-'
        })()

        // Compute product tags array for display
        const productTagsArray = (() => {
            const raw = lead.pitched_products as unknown
            if (!productMap) return []
            const ids: number[] = Array.isArray(raw)
                ? (raw as unknown[])
                      .map((item) => Number(item))
                      .filter((productId) => Number.isFinite(productId))
                : typeof raw === 'string'
                  ? (() => {
                        const trimmedString = String(raw).trim()
                        if (trimmedString.startsWith('[') && trimmedString.endsWith(']')) {
                            try {
                                const arr = JSON.parse(trimmedString)
                                return Array.isArray(arr)
                                    ? arr
                                          .map((item) => Number(item))
                                          .filter((productId) => Number.isFinite(productId))
                                    : []
                            } catch {}
                        }
                        return trimmedString
                            .split(',')
                            .map((token) => Number(token.trim()))
                            .filter((productId) => Number.isFinite(productId))
                    })()
                  : typeof raw === 'number'
                    ? [raw]
                    : []
            if (!ids.length) return []
            return ids
                .map((id) => productMap.get(id))
                .filter((item): item is string => !!item && String(item).trim().length > 0)
        })()

        const formatDate = (dateString: string | null | undefined): string => {
            if (!dateString) return '-'
            const formatted = this.datePipe.transform(dateString, NAVIGATOR_DATE_DISPLAY_FORMAT)
            return formatted || '-'
        }

        // Extract city from address_line (if available) or use territory_name
        const getCity = (): string => {
            // Try to extract from address_line if it contains city info
            // Otherwise use territory_name or default
            return lead.territory_name || '-'
        }

        return {
            id: String(lead.lead_id ?? lead.id ?? ''),
            org_code: String(lead.customer_id ?? ''),
            order_id: lead.order_id ?? null,
            order_status: lead.order_status ?? null,
            schoolName: textOrNA(lead.organisation_name),
            statusLabel: lead.current_stage?.stage_name?.trim() || LEAD_STAGES.PROSPECTING,
            timeframe: textOrNA(lead.current_stage?.duration_display),
            products: productsDisplay,
            principal: textOrNA(lead.principal_name),
            principal_email: textOrNA(lead.principal_email),
            contact: textOrNA(lead.principal_number),
            stage_name: lead.current_stage?.stage_name ?? LEAD_STAGES.PROSPECTING,
            stage: matchedStage,
            lead_status: lead.lead_status ?? null,
            // Additional fields for card display
            clientType: 'Corporate - CSR', // Default - should be mapped from organisation_type if available
            engagement: lead.engagement_type || 'Outbound',
            source: 'Events', // Default - should be mapped from source field if available in API
            city: getCity(),
            date: formatDate(lead.created_at),
            deals: lead.order_id ? 1 : 0, // Count deals based on order_id existence
            productTags: productTagsArray,
        }
    }

    getReportingUsers = (): Observable<ITeamMember[]> => {
        return this.http.get<{ teamMembers?: ITeamMember[] }>('/assets/mock/rmData.json').pipe(
            map((response) => response.teamMembers ?? []),
            catchError((error) => {
                return of([])
            }),
        )
        /** will be replaced with the original API during the API integration  */
    }

    getDepartments = (): Observable<IDepartment[]> => {
        return this.http.get<{ departments?: IDepartment[] }>('/assets/mock/departments.json').pipe(
            map((response) => response.departments ?? []),
            catchError((error) => {
                return of([])
            }),
        )
        /** will be replaced with the original API during the API integration  */
    }

    getTimeSlots = (): Observable<IVisitTimeSlot[]> => {
        return this.http.get<{ timeSlots?: IVisitTimeSlot[] }>('/assets/mock/timeSlots.json').pipe(
            map((response) => response.timeSlots ?? []),
            catchError((error) => {
                return of([])
            }),
        )
        /** will be replaced with the original API during the API integration  */
    }

    getVisitObjectives = (): Observable<IVisitObjective[]> => {
        return this.http
            .get<{ visitObjectives?: IVisitObjective[] }>('/assets/mock/visitObjectives.json')
            .pipe(
                map((response) => response.visitObjectives ?? []),
                catchError((error) => {
                    return of([])
                }),
            )
        /** will be replaced with the original API during the API integration  */
    }

    /** Fetch unassigned clients for a ZM user */
    getUnassignedClients(
        userId: string,
        page: number,
        limit: number,
        searchTerm?: string,
    ): Observable<{
        data: IUnassignedClient[]
        pagination: { page: number; limit: number; total: number; totalPages: number }
    }> {
        const baseUrl = `${environment.apiBaseUrl}/api/aggregated/v1/unassigned-clients/${encodeURIComponent(userId)}`
        let url = `${baseUrl}?page=${encodeURIComponent(String(page))}&limit=${encodeURIComponent(String(limit))}`
        const trimmedSearch = searchTerm?.trim()
        if (trimmedSearch) {
            url += `&searchTerm=${encodeURIComponent(trimmedSearch)}`
        }
        return this.http.get<IUnassignedClientsApiResponse>(url).pipe(
            map((resp) => ({
                data: (resp.data || []).map((item) => this.mapUnassignedClient(item)),
                pagination: this.normalizeUnassignedClientsPagination(resp.pagination, page, limit),
            })),
        )
    }

    private normalizeUnassignedClientsPagination(
        raw: IUnassignedClientsPaginationApi | null | undefined,
        requestedPage: number,
        requestedLimit: number,
    ): { page: number; limit: number; total: number; totalPages: number } {
        const unassignedData = raw ?? {}
        const resolvedPage = Number(
            unassignedData.page ?? unassignedData.current_page ?? requestedPage,
        )
        const resolvedLimit = Number(
            unassignedData.limit ??
                unassignedData.leads_per_page ??
                unassignedData.per_page ??
                requestedLimit,
        )
        const total = Number(
            unassignedData.total ?? unassignedData.total_leads ?? unassignedData.total_count ?? 0,
        )
        let totalPages = Number(unassignedData.totalPages ?? unassignedData.total_pages ?? NaN)
        const limitForMath =
            Number.isFinite(resolvedLimit) && resolvedLimit > 0 ? resolvedLimit : requestedLimit
        if (!Number.isFinite(totalPages) || totalPages < 1) {
            totalPages = limitForMath > 0 ? Math.max(1, Math.ceil(total / limitForMath)) : 1
        }
        return {
            page: Number.isFinite(resolvedPage) && resolvedPage > 0 ? resolvedPage : requestedPage,
            limit:
                Number.isFinite(resolvedLimit) && resolvedLimit > 0
                    ? resolvedLimit
                    : requestedLimit,
            total,
            totalPages,
        }
    }

    /** Map API unassigned item to UI model and handle nulls */
    private mapUnassignedClient(item: IUnassignedClientApiItem): IUnassignedClient {
        return {
            organisationCode: String(item.organisation_code ?? ''),
            organisationName: item.organisation_name ?? '-',
            city: item.city ?? '-',
            state: item.state ?? '-',
            addressLine: item.address_line ?? '-',
            location: item.location ?? '-',
            createdAt: item.created_at ?? '-',
            selected: false,
        }
    }

    /**
     * Reassign leads to a new team member
     * Endpoint: PATCH ${environment.apiBaseUrl}${API_ENDPOINTS.AGGREGATED_LEAD_ASSIGN}
     */
    reassignLeads(request: ILeadReassignmentRequest): Observable<ILeadReassignmentResponse> {
        return this.http
            .patch<ILeadReassignmentResponse>(this.aggregatedLeadAssignUrl, request)
            .pipe(catchError((error) => throwError(() => error)))
    }

    /**
     * Helper method to create reassignment request from UI data
     */
    createReassignmentRequest(
        leadIds: string[],
        newAssignedTo: string,
        territoryId: number,
        reassignedBy: string,
        reason: string,
    ): ILeadReassignmentRequest {
        return {
            lead_ids: leadIds.map((id) => parseInt(id, 10)),
            new_assigned_to: newAssignedTo,
            territory_id: territoryId,
            reassigned_by: reassignedBy,
            reason: reason,
        }
    }

    /**
     * Update the status of a lead (Mark as Lost, Deferred, or Reactivate)
     * Endpoint: PATCH /api/lead-management/v1/leads/:leadId/status
     */
    updateLeadStatus(
        leadId: number,
        request: IUpdateLeadStatusRequest,
    ): Observable<IUpdateLeadStatusResponse> {
        const url = `${environment.apiUrl}${API_ENDPOINTS.LEAD_MANAGEMENT}/${leadId}/status`
        return this.http
            .patch<IUpdateLeadStatusResponse>(url, request, {
                withCredentials: true,
            })
            .pipe(catchError((error) => throwError(() => error)))
    }

    /**
     * Assign unassigned client to a team member (POST API)
     */
    assignClient(request: ILeadAssignmentRequest): Observable<ILeadAssignmentResponse> {
        return this.http
            .post<ILeadAssignmentResponse>(this.aggregatedLeadAssignUrl, request)
            .pipe(catchError((error) => throwError(() => error)))
    }

    /**
     * Helper method to create assignment request from UI data
     */
    createAssignmentRequest(
        clientReferenceId: number,
        territoryId: number,
        assignedTo: string,
        assignedBy: string,
        clientType: number,
        engagementType: number,
        academicYear?: number,
        interactedProductIds?: number[],
        additionalNotes?: string,
    ): ILeadAssignmentRequest {
        const request: ILeadAssignmentRequest = {
            client_reference_id: clientReferenceId,
            territory_id: territoryId,
            assigned_to: assignedTo,
            assigned_by: assignedBy,
            client_type: clientType,
            engagement_type: engagementType,
        }

        // Add optional fields for lead creation
        if (academicYear !== undefined) {
            request.academic_year = academicYear
        }
        if (interactedProductIds?.length) {
            request.interacted_product_ids = interactedProductIds
        }
        if (additionalNotes !== undefined && additionalNotes.trim()) {
            request.additional_notes = additionalNotes.trim()
        }

        return request
    }
}
